<template>
	<v-container class="index pa-0" fluid>
		<v-row class="ma-0">
			<!-- Left Sidebar Toolbar - Full height -->
			<v-col cols="3" v-if="isLogin" class="sidebar-col pa-0">
				<div class="sidebar-container">
					<div class="sidebar-header">
						<v-icon icon="mdi-tools" class="me-2" />
						<span class="sidebar-title">Actions</span>
					</div>
					<div class="sidebar-content">
						<v-btn
							block
							color="white"
							variant="text"
							@click="logout"
							prepend-icon="mdi-logout"
							class="sidebar-btn mb-3"
						>
							Sign out
						</v-btn>
						<v-btn
							block
							color="white"
							variant="text"
							@click="testpost"
							prepend-icon="mdi-post"
							class="sidebar-btn mb-3"
						>
							Test Post
						</v-btn>
						<v-btn
							block
							color="white"
							variant="text"
							@click="testquery"
							prepend-icon="mdi-database-search"
							class="sidebar-btn mb-3"
						>
							Test Query
						</v-btn>
						<v-btn
							block
							color="white"
							variant="text"
							@click="getUserV2"
							prepend-icon="mdi-account-details"
							class="sidebar-btn"
						>
							Get User V2
						</v-btn>
					</div>
				</div>
			</v-col>

			<!-- Main Content - Responsive width -->
			<v-col :cols="isLogin ? 9 : 12" class="main-content-col">
				<div class="main-content">
					<!-- Header Section -->
					<div class="content-header">
						<h1 class="main-title">Account System Login Demo</h1>
					</div>
					
					<!-- Alerts Section -->
					<div class="content-section">
						<v-alert
							v-if="errMsg"
							:type="isLogin ? 'success' : 'info'"
							class="mb-4"
						>
							{{ errMsg }}
						</v-alert>

						<v-alert
							type=""
							color="blue"
							class="mb-4"
							variant="tonal"
						>
							<div class="d-flex align-center">
								<v-icon icon="mdi-earth" class="me-2" />
								<span class="font-weight-medium">Zone: {{ zone }}</span>
							</div>
						</v-alert>

						<v-alert
							v-if="isLogin"
							type=""
							class="mb-4"
							color="green"
							variant="tonal"
						>
							<div class="user-info-row mb-2">
								<v-icon icon="mdi-account" class="me-2" />
								<span class="font-weight-medium">User: {{ getCurrentUser() }}</span>
							</div>
							<div class="user-info-row">
								<v-icon icon="mdi-account-cog" class="me-2" />
								<span class="font-weight-medium">Account Type: {{ account_type }}</span>
							</div>
						</v-alert>
					</div>

					<!-- User Information Section -->
					<template v-if="isLogin">
						<div class="content-section">
							<div class="welcome-section">
								<h2 class="welcome-title">Welcome, {{ display_name }}!</h2>
								<div class="user-details-grid">
									<div class="detail-item">
										<v-icon icon="mdi-account" class="detail-icon" />
										<div class="detail-content">
											<div class="detail-label">User ID</div>
											<div class="detail-value">{{ userid }}</div>
										</div>
									</div>
									<div class="detail-item">
										<v-icon icon="mdi-account-details" class="detail-icon" />
										<div class="detail-content">
											<div class="detail-label">Username</div>
											<div class="detail-value">{{ username }}</div>
										</div>
									</div>
									<div class="detail-item">
										<v-icon icon="mdi-account-group" class="detail-icon" />
										<div class="detail-content">
											<div class="detail-label">Main Account ID</div>
											<div class="detail-value">{{ main_account_id }}</div>
										</div>
									</div>
									<div class="detail-item">
										<v-icon icon="mdi-translate" class="detail-icon" />
										<div class="detail-content">
											<div class="detail-label">Language</div>
											<div class="detail-value">{{ language }}</div>
										</div>
									</div>
									<div class="detail-item">
										<v-icon icon="mdi-map-marker" class="detail-icon" />
										<div class="detail-content">
											<div class="detail-label">Country</div>
											<div class="detail-value">{{ country }}</div>
										</div>
									</div>
									<div class="detail-item">
										<v-icon icon="mdi-clock-outline" class="detail-icon" />
										<div class="detail-content">
											<div class="detail-label">Timezone</div>
											<div class="detail-value">{{ timezone }}</div>
										</div>
									</div>
									<div class="detail-item">
										<v-icon icon="mdi-shield-check" class="detail-icon" />
										<div class="detail-content">
											<div class="detail-label">GDPR</div>
											<div class="detail-value">{{ gdpr ? 'Enabled' : 'Disabled' }}</div>
										</div>
									</div>
									<div class="detail-item">
										<v-icon icon="mdi-account-cog" class="detail-icon" />
										<div class="detail-content">
											<div class="detail-label">Account Type</div>
											<div class="detail-value">{{ account_type }}</div>
										</div>
									</div>
									<div class="detail-item">
										<v-icon icon="mdi-domain" class="detail-icon" />
										<div class="detail-content">
											<div class="detail-label">Tenant</div>
											<div class="detail-value">{{ tenant }}</div>
										</div>
									</div>
									<div class="detail-item">
										<v-icon icon="mdi-earth" class="detail-icon" />
										<div class="detail-content">
											<div class="detail-label">Issuer Zone</div>
											<div class="detail-value">{{ issuer_zone }}</div>
										</div>
									</div>
								</div>
							</div>
						</div>
					</template>

					<!-- Login Form Section -->
					<template v-else>
						<div class="content-section">
							<div class="login-section">
								<h2 class="section-title">Login Parameters</h2>
								<v-form @submit.prevent="login" class="login-form">
									<v-text-field
										v-model="state"
										label="State"
										placeholder="anything..."
										variant="outlined"
										class="mb-3"
									/>
									<v-text-field
										v-model="prompt"
										label="Prompt"
										placeholder="login"
										variant="outlined"
										class="mb-3"
									/>
									<v-text-field
										v-model="login_hint"
										label="Login Hint"
										placeholder="user's account"
										variant="outlined"
										class="mb-3"
									/>
									<v-text-field
										v-model="region"
										label="Region"
										placeholder="eu/us/tw"
										variant="outlined"
										class="mb-3"
									/>
									<v-text-field
										v-model="redirect_url"
										label="Redirect URL"
										placeholder="window.location.origin"
										variant="outlined"
										class="mb-4"
									/>
									<v-btn
										block
										color="primary"
										size="large"
										type="submit"
										prepend-icon="mdi-login"
										class="login-btn"
									>
										Sign in
									</v-btn>
								</v-form>
							</div>
						</div>
					</template>
				</div>
			</v-col>
		</v-row>
	</v-container>
</template>

<style scoped>
.index {
	max-width: 1200px;
	margin: 0 auto;
	padding: 24px;
}

.v-card {
	transition: transform 0.2s;
}

.v-card:hover {
	transform: translateY(-2px);
}

.v-btn {
	margin-bottom: 8px;
}

.user-info-item {
	display: flex;
	align-items: center;
	position: relative;
}

.user-info-content {
	margin-left: 16px;
	display: flex;
	flex-direction: row;
	align-items: center;
	gap: 8px;
}

.user-info-title {
	font-weight: bold;
	font-size: 0.875rem;
	color: rgba(var(--v-theme-on-surface), 0.6);
	display: flex;
	align-items: center;
	min-width: 120px;
}

.user-info-item .v-list-item-subtitle {
	font-size: 1rem;
	color: rgba(var(--v-theme-on-surface), 0.87);
	display: flex;
	align-items: center;
	text-align: left;
}

/* Ensure icons are vertically centered with text */
.v-alert .d-flex.align-center {
	align-items: center !important;
	line-height: 1.5;
}

.v-alert .d-flex.align-center .v-icon {
	display: flex;
	align-items: center;
	justify-content: center;
	flex-shrink: 0;
}

.v-alert .d-flex.align-center span {
	display: flex;
	align-items: center;
	line-height: 1.5;
}

.user-info-row {
	display: flex;
	align-items: center;
	min-height: 24px;
}

.user-info-row .v-icon {
	display: flex;
	align-items: center;
	justify-content: center;
	flex-shrink: 0;
}

.user-info-row span {
	display: flex;
	align-items: center;
}

/* Sidebar styling */
.sidebar-col {
	flex: 0 0 25% !important;
	max-width: 300px !important;
	min-width: 250px !important;
	padding: 0 !important;
}

.sidebar-container {
	position: fixed;
	top: 0;
	left: 0;
	width: 25%;
	max-width: 300px;
	height: 100vh;
	background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
	display: flex;
	flex-direction: column;
	z-index: 100;
}

.sidebar-header {
	padding: 24px 20px 16px 20px;
	border-bottom: 1px solid rgba(255, 255, 255, 0.1);
	display: flex;
	align-items: center;
}

.sidebar-title {
	color: white;
	font-size: 1.25rem;
	font-weight: 600;
}

.sidebar-content {
	padding: 20px;
	flex: 1;
}

.sidebar-btn {
	justify-content: flex-start !important;
	text-align: left !important;
	padding: 12px 16px !important;
	border-radius: 8px !important;
	transition: all 0.3s ease !important;
	text-transform: none !important;
	font-weight: 500 !important;
}

.sidebar-btn:hover {
	background-color: rgba(255, 255, 255, 0.1) !important;
	transform: translateX(4px);
}

.sidebar-btn .v-icon {
	margin-right: 12px !important;
}

/* Main content styling */
.main-content-col {
	flex: 1;
	min-width: 0;
}

.main-content {
	padding: 24px;
	display: flex;
	flex-direction: column;
	align-items: center;
	width: 100%;
	height: 100vh;
	overflow-y: auto;
}

.content-header {
	text-align: center;
	margin-bottom: 24px;
	width: 100%;
}

.main-title {
	font-size: 2.5rem;
	font-weight: 700;
	color: #2c3e50;
	margin: 0;
}

.content-section {
	margin-bottom: 24px;
	width: 100%;
	max-width: 1200px;
}

.welcome-section {
	background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
	border-radius: 16px;
	padding: 24px;
	border: 1px solid #dee2e6;
	width: 100%;
}

.welcome-title {
	font-size: 1.75rem;
	font-weight: 600;
	color: #495057;
	margin-bottom: 20px;
	text-align: center;
}

.user-details-grid {
	display: grid;
	grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
	gap: 16px;
	width: 100%;
}

.detail-item {
	display: flex;
	align-items: center;
	padding: 16px;
	background: white;
	border-radius: 12px;
	border: 1px solid #e9ecef;
	transition: all 0.3s ease;
}

.detail-item:hover {
	transform: translateY(-2px);
	box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.detail-icon {
	color: #667eea;
	margin-right: 16px;
	font-size: 1.5rem;
}

.detail-content {
	flex: 1;
}

.detail-label {
	font-size: 0.875rem;
	font-weight: 600;
	color: #6c757d;
	margin-bottom: 4px;
	text-transform: uppercase;
	letter-spacing: 0.5px;
}

.detail-value {
	font-size: 1rem;
	font-weight: 500;
	color: #495057;
}

.login-section {
	background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
	border-radius: 16px;
	padding: 32px;
	border: 1px solid #dee2e6;
	width: 100%;
	max-width: 600px;
	margin: 0 auto;
}

.section-title {
	font-size: 1.75rem;
	font-weight: 600;
	color: #495057;
	margin-bottom: 24px;
	text-align: center;
}

.login-form {
	width: 100%;
	max-width: 400px;
	margin: 0 auto;
}

.login-btn {
	margin-top: 20px;
	height: 48px;
	font-size: 1.1rem;
	font-weight: 600;
}

@media (max-width: 959px) {
	.sidebar-container {
		width: 200px;
		max-width: 200px;
	}
	.sidebar-col {
		flex: 0 0 200px !important;
		max-width: 200px !important;
		min-width: 180px !important;
	}
}

@media (max-width: 599px) {
	.sidebar-container {
		width: 180px;
		max-width: 180px;
	}
	.sidebar-col {
		flex: 0 0 180px !important;
		max-width: 180px !important;
		min-width: 160px !important;
	}
}
</style>

<script>
import LoginManager from '@synology-hct/syno-c2-account-js-sdk'
import { env } from '../utils/env.js'
export default {
	name: 'Index',
	data() {
		return {
			state: '',
			prompt: '',
			region: '',
			login_hint: '',
			redirect_url: '',
			errMsg: "",
			isLogin: false,
			username: "",
			display_name: null,
			tenant: null,
			issuer_zone: null,
			userid: null,
			main_account_id: null,
			language: "",
			timezone: "",
			country: "",
			gdpr: false,
			account_type: null,
			zone: env.ZONE,
			loginManagerHost: env.LOGIN_MANAGER_HOST,
			apiBasePath: env.API_BASE_PATH,
			globalSSOHost: env.GLOBAL_SSO_HOST,
			logLevel: env.DEBUG ? 'debug' : 'info'
		}
	},
	async mounted() {
		if (env.DEBUG) {
			// print runtime variables
			for (const key in env) { 
				console.log(key+":", env[key])
			}
		}
		// overwrite url for getting query params since vue-router hash mode treat redirect_URL with query params as search
		// reference: https://github.com/vuejs/vue-router/issues/2125#issuecomment-519521424
		/*if (location.search) {
			location.replace(location.pathname + location.hash + location.search)
		}
		if (this.$route.query.error) {
			this.errMsg = this.$route.query.error
			if (this.$route.query.error_description) {
				this.errMsg += ": " + this.$route.query.error_description
			}
			if (this.$route.query.error_uri) {
				this.errMsg += ": " + this.$route.query.error_uri
			}
		}*/

		await LoginManager.configure({ 
			zone: this.zone,
			host: this.loginManagerHost, 
			basePath: this.apiBasePath,
			storage: window.localStorage,
			globalSSOHost: this.globalSSOHost,
			logLevel: this.logLevel,
		})
		if (env.DEBUG) {
			console.log("LoginManager configure completed")
		}

		/* eslint-disable */
		let uri = window.location.search.substring(1)
		let params = new URLSearchParams(uri)
		if (window.location.pathname === "/login") {
			this.$router.push({
				name: "Callback"
			})
		} else if (window.location.pathname === "/loginform") {
			this.$router.push({
				name: "Loginform"
			})
		} else if (LoginManager.hasWebAccessCookie()) {
			this.userApi()
		}
	},
	methods: {
		userApi: async function() {
			const user = await this.getUser()

			if (user) {
				this.isLogin = true;
				this.display_name = user.name
				this.username = user.username
				this.userid = user.id
				this.email = user.email
				this.main_account_id = user.main_account_id
				this.account_type = user.account_type
				this.tenant = user.tenant
				this.issuer_zone = user.issuer_zone
				this.language = user.language
				this.country = user.country
				this.timezone= user.timezone
				this.gdpr = user.gdpr
			}
		},
		getCurrentUser() {
			if (!this.isLogin) {
				return ''
			}
			switch(this.account_type) {
				case 'c2':
					return `${this.username}@${this.tenant}.c2.${this.issuer_zone}`
				default:
					return this.email
			}
		},
		getUser: async function() {
			const resp = await LoginManager.sendRequest('/v1/user')

			if (200 === resp.status) {
				return await resp.json()
			}

			return null
		},
		login: async function() {
			const redirect_to = await LoginManager.login({
					doRedirect:false,
					queries: {
						service_key: "app1",
					...({ redirect_url: this.redirect_url || window.location.origin }),
					...(this.state ? { state: this.state } : { state: 'teststate123' }),
					...(this.prompt ? { prompt: this.prompt } : {}),
					...(this.region ? { region: this.region } : {}),
					...(this.login_hint ? { login_hint: this.login_hint } : {}),
				}
			})
			window.location = redirect_to + "&login_portal=1"
			await new Promise(() => {})
		},
		logout: async function() {
			await LoginManager.logout({queries: {redirect_url: window.location.origin, state: "test_logout_state"}})
		},
		testpost: async function() {
			const resp = await LoginManager.sendRequest('/v1/testpost', {
				method: 'POST',
				body: JSON.stringify({
					b1: 123,
					b2: false,
					b3: {
						b4: 'fuck',
					},
				}),
			})

			if (200 === resp.status) {
				console.log(await resp.json())
			}
		},
		testquery: async function() {
			const resp = await LoginManager.sendRequest('/v1/user?q=123')

			if (200 === resp.status) {
				console.log(await resp.json())
			}
		},
		getUserV2: async function() {
			const resp = await LoginManager.sendRequest('/backend/auth/api/v2/user')
			if (200 === resp.status) {
				console.log(await resp.json())
			}
		}
	}
}
</script>
